// RightPanel - 選択アセットのプロパティパネル
// プレビュー + 回転 + 削除

import { useDispatch, useSelector } from "react-redux";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { RootState } from "app/store";
import Constants from "common/constant";
import { usePlaySound } from "common/utils";
import { useActionCommand } from "features/builder/hook";
import { useBuilderScene } from "features/builder/context";
import { ActionCommandType } from "features/builder/types";
import { actions, selectMapModelById } from "features/builder/slice";
import { createBottomGround } from "features/builder/canvas/scene/util";

export const RightPanel = () => {
  const play = usePlaySound();
  const dispatch = useDispatch();
  const scene = useBuilderScene();
  const { handleAction } = useActionCommand();

  const selectedPlacedModelId = useSelector(
    (state: RootState) => state.builder.selectedPlacedModelId,
  );
  const selectedModel = useSelector((state: RootState) =>
    selectedPlacedModelId ? selectMapModelById(state, selectedPlacedModelId) : null,
  );

  if (!selectedModel) return null;

  const getSelectedMesh = (): Mesh | null => {
    if (!scene || !selectedPlacedModelId) return null;
    return scene.getMeshById(selectedPlacedModelId) as Mesh | null;
  };

  const handleRotate = () => {
    play();
    const mesh = getSelectedMesh();
    if (mesh?.parent) {
      (mesh.parent as TransformNode).addRotation(0, Math.PI / 2, 0);
    }
  };

  const handleDelete = () => {
    play();
    const mesh = getSelectedMesh();
    if (mesh && selectedPlacedModelId && scene) {
      const outer = mesh.parent as TransformNode;
      const boundingInfo = mesh.getBoundingInfo();

      // 地面を補填
      createBottomGround(scene, boundingInfo.boundingBox, outer.position);

      // Undo/Redo 用にアクション記録
      handleAction({
        type: ActionCommandType.REMOVE_MODE,
        meshId: selectedPlacedModelId,
      });

      // 3D シーンから削除
      outer.dispose();

      // Redux から削除
      dispatch(actions.removeModelFromMap(selectedPlacedModelId));
      dispatch(actions.selectPlacedModel(null));
    }
  };

  // アセット画像のパス
  const assetImagePath = `${Constants.assetHost}/assets/models/${selectedModel.filename.replace(".gltf.glb", ".png").replace(".glb", ".png")}`;

  return (
    <div className="flex-col-el absolute right-3 top-1/2 -translate-y-1/2 z-[10000] pointer-events-auto">
      <div className="flex-col-el gap-4 p-4 rounded-2xl bg-white/80 backdrop-blur-sm border-2 border-white shadow-lg">
        {/* プレビュー */}
        <div className="flex-col-el flex-center w-28 h-28 rounded-xl bg-gray-100 overflow-hidden">
          <img
            src={assetImagePath}
            className="w-full h-full object-contain"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = "none";
            }}
          />
        </div>

        {/* 区切り線 */}
        <div className="w-full h-[2px] bg-gray-200 rounded-full" />

        {/* 操作ボタン */}
        <div className="flex-col-el gap-2">
          <button
            onClick={handleRotate}
            className="flex-col-el flex-center w-28 h-16 rounded-xl transition-all duration-150 active:scale-95 cursor-pointer bg-blue-100 hover:bg-blue-200"
          >
            <RotateRightIcon className="text-blue-600" style={{ fontSize: 36 }} />
          </button>
          <button
            onClick={handleDelete}
            className="flex-col-el flex-center w-28 h-16 rounded-xl transition-all duration-150 active:scale-95 cursor-pointer bg-red-100 hover:bg-red-200"
          >
            <DeleteOutlineIcon className="text-red-600" style={{ fontSize: 36 }} />
          </button>
        </div>
      </div>
    </div>
  );
};
